import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AppDatePickerLocalization } from "@/components/AppDatePickerLocalization";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { formatIsoDate } from "@/utils/date-format";

type ServiceDateControlProps = {
  value: string;
  onChange: (isoDate: string) => void;
  label?: string;
};

export default function ServiceDateControl({
  value,
  onChange,
  label = "Service date",
}: ServiceDateControlProps) {
  let parsed: Dayjs | null = null;
  if (value) {
    const next = dayjs(value);
    if (next.isValid()) {
      parsed = next.startOf("day");
    }
  }
  const baseDay = parsed ?? dayjs().startOf("day");

  const handleChange = (newValue: Dayjs | null) => {
    if (newValue == null || !newValue.isValid()) {
      return;
    }
    onChange(formatIsoDate(newValue));
  };

  const shiftByDays = (delta: number) => {
    const next = baseDay.add(delta, "day");
    if (next.isValid()) {
      onChange(formatIsoDate(next));
    }
  };

  return (
    <AppDatePickerLocalization>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap" }}>
        <IconButton
          type="button"
          size="small"
          color="inherit"
          onClick={() => shiftByDays(-1)}
          aria-label="Previous day"
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: 280 }}>
          <DatePicker
            label={label}
            value={parsed}
            onChange={handleChange}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
              },
            }}
          />
        </Box>
        <IconButton
          type="button"
          size="small"
          color="inherit"
          onClick={() => shiftByDays(1)}
          aria-label="Next day"
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>
    </AppDatePickerLocalization>
  );
}
